/**
 * 🔒 The Deal Pack (piece 19; decision 5400; row T2744): the four inputs the
 * owner's controls-scope-proposal skill reads, written out of one Deal as plain
 * text with Markdown headings. Copy for Claude puts the text on the clipboard,
 * and Download Deal Pack zips the same text beside the Deal's photos, files and
 * PDFs.
 *
 * 🔨 The opening line, the five headings and the labels under them are the
 * skill's own words and are never passed through `t()`: the skill reads them,
 * and a translated heading is one it would not find.
 */

/** The one line the pack opens on, naming the skill that reads it. */
export const DEAL_PACK_OPENING_LINE = 'Use the controls-scope-proposal skill on the pack below';

/** The pack's headings, in the order the skill reads them. */
export const DEAL_PACK_HEADINGS = ['Customer', 'Customer Spec', 'Sales Meeting Notes', 'Photos', 'Existing Program, I/O and Drawings'] as const;

/** 🔒 Decision 5530: the heading under Customer Spec over the questions from Claude that have an answer. */
export const DEAL_PACK_ANSWERED_HEADING = 'Answered Questions';
/** 🔒 Decision 5530: the pack's own last heading, over the questions from Claude still open; drawn only while one is. */
export const DEAL_PACK_OPEN_QUESTIONS_HEADING = 'Open Questions';

/** The copy route: the pack's text, and the Markdown file's name the zip carries it under. */
export const dealPackPath = (dealId: string): string => `/deals/${dealId}/pack`;
/** The download route: the zip itself, named for the Deal. */
export const dealPackZipPath = (dealId: string): string => `/deals/${dealId}/pack.zip`;

/** What the copy route answers. */
export interface DealPackAnswer {
  text: string;
  filename: string;
}

/** A dated, signed note as the pack writes it: the day in the organization's zone, who wrote it, and its words. */
export interface DealPackNote {
  day: string;
  author: string;
  text: string;
  /** A meeting's own subject, where the entry is a meeting rather than a note. */
  meeting?: string | null;
}

/** Everything the pack's text reads, gathered by the server under the viewer's own gates. */
export interface DealPackFacts {
  dealName: string;
  dealType: string;
  companyName: string;
  site: { name: string | null; address: string | null } | null;
  primaryContact: { name: string; title: string | null } | null;
  /** 🔒 Decision 6901: every Need note tagged to the Deal, newest first, each in the person's own words. */
  needNotes: readonly DealPackNote[];
  budget: { target: string | null; maximum: string | null } | null;
  budgetNotes: readonly DealPackNote[];
  authority: readonly { name: string; role: string; title: string | null; note: string | null }[];
  authorityNotes: readonly DealPackNote[];
  timing: { expectedQuote: string | null; expectedStart: string | null; expectedFinish: string | null };
  timingNotes: readonly DealPackNote[];
  /** The Deal's notes and meetings, newest first. */
  meetingNotes: readonly DealPackNote[];
  /**
   * 🔒 Each existing-equipment photo carries its kind, its position and its unit, the position group it stands in
   * (decision 5440); any other image carries its name and description alone.
   */
  photos: readonly DealPackPhoto[];
  files: readonly { name: string; kind: string }[];
  /**
   * 🔒 Each Discovery template the Deal holds with its answered questions, label and value, newest answer each
   * (decision 5461); a template with no answer is left out.
   */
  discovery?: readonly { template: string; answers: readonly { label: string; value: string }[] }[];
  /**
   * 🔒 The questions Ask Claude wrote onto the Deal (decision 5530), in the order they were asked: an answered one
   * stands under Customer Spec with its newest answer, and an open one under the pack's own Open Questions heading.
   */
  askClaude?: readonly { question: string; answer: string | null }[];
}

/** 🔒 How the text is written: whole for Copy for Claude and the zip, or as Ask Claude sends it (decision 5530). */
export interface DealPackTextOptions {
  /**
   * 🔨 Ask Claude's text: the opening line naming the skill is left out, because the call carries the application's
   * own instruction, and Budget's heading, its Target and Maximum and its notes are left out whole.
   */
  forAskClaude?: boolean;
}

/** One photo as the pack's Photos section lists it. */
export interface DealPackPhoto {
  name: string;
  note: string | null;
  kind?: string | null;
  /** The position as a person reads it, six decimals each; null where none was recorded. */
  position?: string | null;
  /** The position group's number, from 1, in the order the groups stand; null where the photo has no position. */
  unit?: number | null;
  /** 🔒 The Discovery spot the photo was filed under, such as *Panel Design / Main Panel*; every file stands under its spot (decision 5461). */
  spot?: string | null;
}

const NONE = 'None recorded.';

/**
 * 🔒 A photo's line (decision 5440): its kind first with its file's name, then its unit and position, then its note,
 * so the skill reads which unit a nameplate belongs to rather than inferring it. A photo with no kind is its name.
 */
export function dealPackPhotoLine(photo: DealPackPhoto): string {
  const note = photo.note ? `: ${photo.note}` : '';
  if (!photo.kind && !photo.spot) return `- ${photo.name}${note}`;
  const place = photo.position ? `, Unit ${photo.unit ?? 1} at ${photo.position}` : photo.kind ? ', no position recorded' : '';
  return `- ${photo.spot ?? photo.kind} (${photo.name})${place}${note}`;
}

/** A leading list marker the person typed, which the numbering replaces; the words after it are kept as written. */
const MARKER = /^(?:[-*•]|\d+[.)])\s+/;

/**
 * 🔒 The Need answer as numbered items (decision 5400): one per paragraph where
 * the text is written in paragraphs, else one per line, each in the person's own
 * words with only a typed list marker taken off.
 */
export function dealPackNeedItems(text: string | null): string[] {
  const trimmed = (text ?? '').replace(/\r\n?/g, '\n').trim();
  if (trimmed === '') return [];
  const paragraphs = trimmed.split(/\n\s*\n/).map((part) => part.trim()).filter((part) => part !== '');
  const pieces = paragraphs.length > 1 ? paragraphs.map((part) => part.replace(/\s*\n\s*/g, ' ')) : trimmed.split('\n').map((part) => part.trim()).filter((part) => part !== '');
  return pieces.map((piece) => piece.replace(MARKER, ''));
}

function noteBlock(note: DealPackNote): string[] {
  const lines = [`### ${note.day} · ${note.author}`];
  if (note.meeting) lines.push(`Meeting: ${note.meeting}`);
  if (note.text.trim() !== '') lines.push(note.text.trim());
  return [...lines, ''];
}

/** 🔒 Decision 6901: each Need note dated and signed, its items numbered on from the note before. */
function needLines(notes: readonly DealPackNote[]): string[] {
  let next = 1;
  const lines = notes.flatMap((note) => {
    const items = dealPackNeedItems(note.text);
    if (items.length === 0) return [];
    return [`### ${note.day} · ${note.author}`, ...items.map((item) => `${next++}. ${item}`), ''];
  });
  return lines.length === 0 ? [NONE] : lines;
}

function notesUnder(notes: readonly DealPackNote[]): string[] {
  return notes.length === 0 ? [] : ['Notes:', '', ...notes.flatMap(noteBlock)];
}

/** One labelled line, or none where the value is missing. */
const labelled = (label: string, value: string | null | undefined): string[] => (value ? [`- ${label}: ${value}`] : []);

/** 🔒 The Discovery answers under Customer Spec (decision 5461): each template a heading, each answer a line reading its label and value. */
function discoveryLines(sections: NonNullable<DealPackFacts['discovery']>): string[] {
  const answered = sections.filter((section) => section.answers.length > 0);
  if (answered.length === 0) return [];
  return ['## Discovery', '', ...answered.flatMap((section) => [`### ${section.template}`, '', ...section.answers.map((answer) => `- ${answer.label}: ${answer.value}`), ''])];
}

/** 🔒 The answered questions from Claude under Customer Spec (decision 5530): each question, and its answer beneath. */
function answeredLines(questions: NonNullable<DealPackFacts['askClaude']>): string[] {
  const answered = questions.filter((row) => row.answer !== null && row.answer.trim() !== '');
  if (answered.length === 0) return [];
  return [`## ${DEAL_PACK_ANSWERED_HEADING}`, '', ...answered.flatMap((row) => [`- ${row.question}`, `  Answer: ${row.answer!.trim().replace(/\s*\n\s*/g, ' ')}`]), ''];
}

/** 🔒 The open questions from Claude under the pack's own last heading (decision 5530), numbered; none drawn while none is open. */
function openLines(questions: NonNullable<DealPackFacts['askClaude']>): string[] {
  const open = questions.filter((row) => row.answer === null || row.answer.trim() === '');
  if (open.length === 0) return [];
  return ['', `# ${DEAL_PACK_OPEN_QUESTIONS_HEADING}`, '', ...open.map((row, index) => `${index + 1}. ${row.question}`)];
}

/**
 * 🔒 The pack's text (decision 5400): the opening line, then Customer, Customer
 * Spec, Sales Meeting Notes, Photos and Existing Program, I/O and Drawings, in
 * that order. An empty section keeps its heading and says none is recorded, so
 * the skill always finds all five. 🔒 The questions from Claude follow
 * (decision 5530): the answered ones under Customer Spec, and the open ones
 * under Open Questions at the foot, a heading drawn only while one is open.
 * 🔨 Ask Claude's text carries no opening line and no Budget at all.
 */
export function dealPackText(facts: DealPackFacts, options: DealPackTextOptions = {}): string {
  const [customer, spec, meetings, photos, files] = DEAL_PACK_HEADINGS;
  const forAskClaude = options.forAskClaude === true;
  const site = facts.site ? [facts.site.name, facts.site.address].filter((part): part is string => Boolean(part)).join(', ') : '';
  const contact = facts.primaryContact ? [facts.primaryContact.name, facts.primaryContact.title].filter(Boolean).join(', ') : '';
  const budget = !forAskClaude && facts.budget ? [facts.budget.target ? `Target ${facts.budget.target}` : null, facts.budget.maximum ? `Maximum ${facts.budget.maximum}` : null].filter(Boolean).join(' · ') : '';
  const timing = [...labelled('Expected quote', facts.timing.expectedQuote), ...labelled('Expected start', facts.timing.expectedStart), ...labelled('Expected finish', facts.timing.expectedFinish)];
  // 🔨 Decision 5530: the Budget pair never leaves the building on Ask Claude's call, so its heading, figures and notes go whole.
  const budgetLines = forAskClaude ? [] : ['## Budget', '', budget || NONE, '', ...notesUnder(facts.budgetNotes)];
  const lines: string[] = [
    ...(forAskClaude ? [] : [DEAL_PACK_OPENING_LINE, '']),
    `# ${customer}`,
    '',
    ...labelled('Company', facts.companyName),
    ...labelled('Site', site || 'none named'),
    ...labelled('Primary contact', contact || 'none named'),
    ...labelled('Deal', facts.dealName),
    ...labelled('Deal type', facts.dealType),
    '',
    `# ${spec}`,
    '',
    '## Need',
    '',
    ...needLines(facts.needNotes),
    '',
    ...budgetLines,
    '## Authority',
    '',
    ...(facts.authority.length === 0 ? [NONE] : facts.authority.map((row) => `- ${[row.name, row.role, row.title].filter(Boolean).join(', ')}${row.note ? `: ${row.note}` : ''}`)),
    '',
    ...notesUnder(facts.authorityNotes),
    '## Timing',
    '',
    ...(timing.length === 0 ? [NONE] : timing),
    '',
    ...notesUnder(facts.timingNotes),
    ...discoveryLines(facts.discovery ?? []),
    ...answeredLines(facts.askClaude ?? []),
    `# ${meetings}`,
    '',
    ...(facts.meetingNotes.length === 0 ? [NONE, ''] : facts.meetingNotes.flatMap(noteBlock)),
    `# ${photos}`,
    '',
    ...(facts.photos.length === 0 ? [NONE] : facts.photos.map(dealPackPhotoLine)),
    '',
    `# ${files}`,
    '',
    ...(facts.files.length === 0 ? [NONE] : facts.files.map((file) => `- ${file.name} (${file.kind})`)),
    ...openLines(facts.askClaude ?? []),
  ];
  return `${lines.join('\n').replace(/\n{3,}/g, '\n\n').trimEnd()}\n`;
}

/** The Deal's name as a file name: nothing a file system reads as a folder or refuses, and never empty. */
export function dealPackBaseName(dealName: string): string {
  return dealName.replace(/[/\\:*?"<>|]/g, '-').replace(/\s+/g, ' ').trim() || 'Deal';
}

/** The Markdown file the zip carries the text in, named for the Deal. */
export const dealPackFileName = (dealName: string): string => `${dealPackBaseName(dealName)}.md`;

/** The zip's own name, named for the Deal. */
export const dealPackZipName = (dealName: string): string => `${dealPackBaseName(dealName)} Deal Pack.zip`;
